'use client';

import React, { FC, useState, useRef, useCallback, useEffect } from 'react';
import { TypeGlobalNavFields, TypeNavGroup } from '@/core/generated-types';
import { Image } from '@/components/ui';
import { NavColumn } from '@/components/nav-v2';
import { useCartUrl } from './hooks/useCartUrl';

const HOVER_DELAY = 150;

export const LargeNav: FC<TypeGlobalNavFields> = ({ logo, groups }) => {
  const cartHref = useCartUrl();
  const [focusedGroup, setFocusedGroup] = useState<TypeNavGroup | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearHoverTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(
    (group?: TypeNavGroup) => {
      clearHoverTimeout();
      if (group) {
        setFocusedGroup(group);
      }
    },
    [clearHoverTimeout],
  );

  const handleMouseLeave = useCallback(() => {
    clearHoverTimeout();
    timeoutRef.current = setTimeout(() => {
      setFocusedGroup(null);
    }, HOVER_DELAY);
  }, [clearHoverTimeout]);

  useEffect(() => {
    return () => {
      clearHoverTimeout();
    };
  }, [clearHoverTimeout]);

  return (
    <>
      {/* Nav bar */}
      <nav
        data-testid="global-nav"
        className="pl-8 pr-4 bg-white border-b border-grey-mid-light hidden md:block"
      >
        <div className="grid grid-cols-[200px_auto_200px] mx-auto h-[80px]">
          {/* Logo */}
          {logo && (
            <div className="w-[166px] flex items-center">
              <Image imageData={logo} alt={logo.fields.altText} />
            </div>
          )}

          {/* Navigation groups */}
          <div className="flex items-center justify-center">
            <div
              className="flex items-center gap-14"
              onMouseLeave={handleMouseLeave}
            >
              {groups?.map((item) => (
                <div
                  className="cursor-pointer hover:text-indigo hover:underline text-base whitespace-nowrap py-3"
                  key={item.sys.id}
                  onMouseEnter={() => handleMouseEnter(item)}
                >
                  {item.fields.title}
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-14 justify-end">
            <a aria-label="Cart" className="p-4" href={cartHref}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="31"
                height="24"
                viewBox="0 0 31 24"
                fill="none"
              >
                <path
                  d="M0.890446 0.0012157C0.653314 0.0012157 0.426866 0.0977854 0.259682 0.269197C0.0925044 0.439401 -0.00116472 0.672379 1.09348e-05 0.912583C0.00120772 1.41354 0.399588 1.81913 0.890455 1.82034H3.43488C4.16523 1.82034 4.79366 2.3068 4.99047 3.02021L5.23827 3.91709V3.9183V3.92071L8.9908 17.5202C9.27773 18.5607 10.0223 19.3767 10.9626 19.7751C10.7527 20.1759 10.633 20.6333 10.633 21.115C10.633 22.6975 11.9147 24 13.4714 24C15.0282 24 16.3099 22.6987 16.3099 21.115C16.3099 20.7371 16.2364 20.375 16.1036 20.0431H19.4543C19.3215 20.375 19.2468 20.7371 19.2468 21.115C19.2468 22.6975 20.5332 24 22.0912 24C23.648 24 24.9297 22.6987 24.9297 21.115C24.9297 20.7371 24.8562 20.375 24.7234 20.0431H26.2291C26.72 20.0406 27.116 19.6363 27.1184 19.1377C27.1172 18.6368 26.72 18.2324 26.2291 18.23H12.2717C11.5413 18.23 10.9058 17.7435 10.709 17.0301L10.6189 16.7017H23.9954C25.7122 16.7017 27.2228 15.5224 27.672 13.8361L30.3208 3.91951V3.92071C30.3943 3.6467 30.3386 3.35338 30.1691 3.12884C29.9995 2.90311 29.7375 2.77155 29.4576 2.77155H6.7749L6.70851 2.53013C6.29707 1.03934 4.95611 0 3.43373 0L0.890446 0.0012157ZM7.27761 4.59175H28.2922L25.9517 13.359C25.711 14.2608 24.9166 14.8824 23.9953 14.8824H10.1159L7.27761 4.59175ZM13.4714 20.0429C14.0631 20.0429 14.5207 20.5137 14.5207 21.1148C14.5207 21.716 14.0631 22.1819 13.4714 22.1819C12.8798 22.1819 12.4162 21.716 12.4162 21.1148C12.4162 20.5137 12.8798 20.0429 13.4714 20.0429ZM22.0923 20.0429C22.6839 20.0429 23.1416 20.5137 23.1416 21.1148C23.1416 21.716 22.6839 22.1819 22.0923 22.1819C21.5006 22.1819 21.037 21.716 21.037 21.1148C21.037 20.5137 21.5006 20.0429 22.0923 20.0429Z"
                  fill="#424140"
                />
              </svg>
            </a>
          </div>
        </div>
      </nav>

      {/* Focused group */}
      {focusedGroup && focusedGroup.fields.columns && (
        <div
          className="bg-white border-b border-grey-mid-light py-8"
          onMouseEnter={() => handleMouseEnter()}
          onMouseLeave={handleMouseLeave}
        >
          <div className="max-w-7xl mx-auto px-8">
            <div
              className="grid gap-10 justify-center"
              style={{
                gridTemplateColumns: `repeat(${Math.min(focusedGroup.fields.columns.length, 5)}, minmax(min-content, 192px))`,
              }}
            >
              {focusedGroup.fields.columns.map((column) => (
                <NavColumn
                  key={column.sys.id}
                  entryName={column.fields.entryName}
                  items={column.fields.items}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(LargeNav);
